import {
  Box,
  Button,
  FormControl,
  Input,
  Textarea,
  Text,
  Avatar,
  Flex,
  FormErrorMessage,
  useToast,
  Spinner,
  Tag,
} from "@chakra-ui/react";
import { RiEdit2Fill } from 'react-icons/ri';
import { CloseIcon } from "@chakra-ui/icons";

import axios from "axios";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import uuid from "react-uuid";
import { formatDate } from "../helpers/formatDate";
import { configFile } from "@/config";

type Comment = {
  author: {
    name: string;
  };
  content: string;
  createdAt: string;
  id: number;
  children?: Comment[];
  isAdminComment?: boolean;
};

interface Props {
  item?: Comment[];
  id: number;
  isPost?: boolean;
  isBreed?: boolean;
}

function Comments({ item, id, isPost, isBreed }: Props) {
  const [emailError, setEmailError] = useState<boolean>(false);
  const [nameError, setNameError] = useState<boolean>(false);
  const [contentError, setContentError] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(false);
  const [replyComment, setReplyComment] = useState<{
    [commentId: number]: boolean;
  }>({});
  const toast = useToast();
  const router = useRouter();

  const handleCommentSubmit = async (e: any) => {
    e.preventDefault();

    setEmailError(e.target.email.value === "");
    setNameError(e.target.name.value === "");
    setContentError(e.target.content.value === "");

    if (
      !(e.target.email.value === "") &&
      !(e.target.name.value === "") &&
      !(e.target.content.value === "")
    ) {
      setIsLoading(true);
      try {
        if (isPost) {
          await axios.post(
            `${process.env.NEXT_PUBLIC_API_URL}/api/comments/api::article.article:${id}`,
            {
              author: {
                id: uuid(),
                name: e.target.name.value,
                email: e.target.email.value,
              },
              content: e.target.content.value,
            }
          )
        } else if (isBreed) {
          await axios.post(
            `${process.env.NEXT_PUBLIC_API_URL}/api/comments/api::breed.breed:${id}`,
            {
              author: {
                id: uuid(),
                name: e.target.name.value,
                email: e.target.email.value,
              },
              content: e.target.content.value,
            }
          )
        }
        toast({
          title: "Obrigado!",
          description: "Seu comentário está em revisão.",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        setIsLoading(false);
        e.target.reset();
      } catch (e) {
        setIsLoading(false);
        toast({
          title: "Erro!",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    }
  };

  const handleReplySubmit = async (e: any, commentId: number) => {
    e.preventDefault();
    const author = {
      id: uuid(),
      name: e.target.name.value,
      email: e.target.email.value,
    };
    const content = e.target.content.value;

    try {
      if (isPost) {
        await axios.post(`${process.env.NEXT_PUBLIC_API_URL}/api/comments/api::article.article:${id}`, {
          author,
          content,
          threadOf: commentId,
        });
      } else if (isBreed) {
        await axios.post(`${process.env.NEXT_PUBLIC_API_URL}/api/comments/api::breed.breed:${id}`, {
          author,
          content,
          threadOf: commentId,
        });
      }
      toast({
        title: 'Obrigado!',
        description: 'Sua resposta está em revisão.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      setIsLoading(false);
      e.target.reset();
    } catch (e) {
      setIsLoading(false);
      toast({
        title: 'Erro!',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  useEffect(() => {
    setEmailError(false);
    setNameError(false);
    setContentError(false);
  }, [router.query.slug]);

  return (
    <Box padding={{ base: "16px", lg: "32px" }} pt="0" mt="28px">
      <form
        onSubmit={(e) => handleCommentSubmit(e)}
        style={{
          display: "flex",
          flexWrap: "wrap",
          rowGap: "24px",
          columnGap: "24px",
        }}
      >
        <FormControl width="100%" isInvalid={contentError}>
          <Textarea
            name="content"
            width="100%"
            rows={5}
            placeholder={isPost ? "Seu comentário" : isBreed ? "Sua pergunta" : ""}
          />
          {contentError && (
            <FormErrorMessage>Texto é obrigatório.</FormErrorMessage>
          )}
        </FormControl>
        <FormControl
          flex="1 2 100%"
          isInvalid={nameError}
          maxWidth={{ base: "100%", md: "calc(50% - 12px)" }}
        >
          <Input name="name" type="text" placeholder="Seu nome" />
          {nameError && <FormErrorMessage>Nome é obrigatório.</FormErrorMessage>}
        </FormControl>
        <FormControl
          flex="1 2 100%"
          isInvalid={emailError}
          maxWidth={{ base: "100%", md: "calc(50% - 12px)" }}
        >
          <Input name="email" type="email" placeholder="Seu e-mail" />
          {emailError && (
            <FormErrorMessage>E-mail é obrigatório.</FormErrorMessage>
          )}
        </FormControl>
        <Button
          mt="8px"
          size="lg"
          borderRadius="44px"
          bgColor={configFile.primaryAccentColor}
          width="100%"
          fontWeight="600"
          color="#fff"
          type="submit"
          _hover={{ bgColor: configFile.secondaryAccentColor }}
        >
          {isPost ? "Envie um comentário" : isBreed ? "Envie uma pergunta" : ""}
        </Button>
        {isLoading && (
          <Spinner
            margin=" 0 auto"
            mt="15px"
            color={configFile.primaryAccentColor}
          />
        )}
      </form>
      {item && (
        <Box mt="16px">
          <Text
            fontWeight="500"
            fontSize="16px"
            lineHeight="24px"
            color={configFile.primaryAccentColor}
            mb="16px"
          >
            {item.length}{" "}
            {isPost ?
              item.length === 1 ? "comentário" : "comentários"
              : isBreed ?
                item.length === 1 ? "pergunta" : "perguntas"
                : ""
            }
          </Text>
          <Flex flexDir="column" rowGap="16px" width="100%">
            {item.map((comment: Comment) => (
              <Box
                key={uuid()}
                borderRadius="6px"
                border="1px solid #E2E8F0"
                padding="20px"
              >
                <Flex alignItems="center">
                  <Avatar name={comment.author.name} mr="16px" size="sm" />
                  <Box>
                    <Text fontWeight="500" fontSize="14px" lineHeight="20px">
                      {comment.author.name}
                    </Text>
                    {comment.isAdminComment && (
                      <Tag size="sm" bgColor="teal.50" color="teal.800">
                        [ADMIN]
                      </Tag>
                    )}
                    <Text fontSize="12px" lineHeight="16px">
                      {formatDate(comment.createdAt)}
                    </Text>
                  </Box>
                </Flex>
                <Text mt="12px" pl={{ base: "20px", md: "48px" }}>
                  {comment.content}
                </Text>
                <Flex gap="16px" mt="20px" pl={{ base: '20px', md: '48px' }}>
                  <Flex
                    color="gray.500"
                    fontSize="12px"
                    lineHeight="16px"
                    fontWeight="600"
                    alignItems="center"
                    gap="4px"
                  >
                    {replyComment[comment.id] ? <CloseIcon /> : <RiEdit2Fill />}
                    <Text
                      color="gray.500"
                      onClick={() =>
                        setReplyComment({
                          ...replyComment,
                          [comment.id]: !replyComment[comment.id],
                        })
                      }
                      cursor="pointer"
                    >
                      {replyComment[comment.id] ? 'Close' : 'Reply'}
                    </Text>
                  </Flex>
                </Flex>
                {replyComment[comment.id] && (
                  <Box mt="16px" pl={{ base: '20px', md: '48px' }}>
                    <form
                      onSubmit={(e) => handleReplySubmit(e, comment.id)}
                      style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        rowGap: '24px',
                        columnGap: '24px',
                      }}
                    >
                      <FormControl width="100%" isInvalid={contentError}>
                        <Textarea
                          name="content"
                          width="100%"
                          rows={5}
                          placeholder="Seu comentário"
                        />
                        {contentError && <FormErrorMessage>Comentário é obrigatório.</FormErrorMessage>}
                      </FormControl>
                      <FormControl
                        flex="1 2 100%"
                        isInvalid={nameError}
                        maxWidth={{ base: '100%', md: 'calc(50% - 12px)' }}
                      >
                        <Input name="name" type="text" placeholder="Seu nome" />
                        {nameError && <FormErrorMessage>Nome é obrigatório.</FormErrorMessage>}
                      </FormControl>
                      <FormControl
                        flex="1 2 100%"
                        isInvalid={emailError}
                        maxWidth={{ base: '100%', md: 'calc(50% - 12px)' }}
                      >
                        <Input name="email" type="email" placeholder="Seu e-mail" />
                        {emailError && <FormErrorMessage>E-mail é obrigatório.</FormErrorMessage>}
                      </FormControl>
                      <Button
                        aria-label="add an answer"
                        mt="8px"
                        size="lg"
                        bgColor={configFile.primaryAccentColor}
                        width="100%"
                        fontWeight="600"
                        color="#FFF"
                        type="submit"
                        _hover={{ bgColor: configFile.secondaryAccentColor }}
                      >
                        Adicionar uma resposta
                      </Button>
                    </form>
                  </Box>
                )}
                {comment.children &&
                  comment.children.map((reply: Comment) => (
                    <Box key={uuid()} borderRadius="6px" padding="20px" mt="16px" pl={{ base: '20px', md: '48px' }}>
                      <Flex alignItems="center" w="100%">
                        <Avatar name={reply.author.name} mr="16px" size="sm" />
                        <Box>
                          <Flex gap="8px">
                            <Text fontWeight="500" fontSize="14px" lineHeight="20px">
                              {reply.author.name}
                            </Text>
                            {reply.isAdminComment && (
                              <Tag size="sm" bgColor="teal.50" color="teal.800">
                                [ADMIN]
                              </Tag>
                            )}
                          </Flex>
                          <Text fontSize="12px" lineHeight="16px">
                            {formatDate(reply.createdAt)}
                          </Text>
                        </Box>
                      </Flex>
                      <Text mt="12px" pl={{ base: '20px', md: '48px' }}>
                        {reply.content}
                      </Text>
                    </Box>
                  ))}
              </Box>
            ))}
          </Flex>
        </Box>
      )}
    </Box>
  );
}

export default Comments;
